import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Play, Pause, RotateCcw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { trackDemo, flushDemoTrack } from '../lib/demoTrack';
import { fetchWithCsrf } from '../lib/fetchWithCsrf';

// Review sandbox only (tenants.is_reviewer): a 38-second intro shown as soon as a reviewer signs in. It explains
// what a NEW tenant goes through in onboarding, which a reviewer skips because their account is pre-built.
//
// - Plays at the video's own size (1920x1080), shrinking only if the window is smaller than that.
// - Shown once per sign-in, not once per page: the "seen" flag is keyed to the session's last_sign_in_at, so a
//   refresh mid-session does not replay it but signing out and back in does. sessionStorage, wrapped in try/catch
//   because it can throw in private windows; if it does, the video simply shows again on the next page load.
// - The file has no audio track, so browsers allow it to autoplay. If one refuses anyway, a Play button appears.
// - Sits above the Terms modal (z-[9999]) so a first-time reviewer sees the video first, Terms right after.
const VIDEO_SRC = `${process.env.PUBLIC_URL || ''}/assets/${encodeURIComponent('Reviewer Intro Video.mp4')}`;
// Prospect demos (tenants.reviewer_kind = 'prospect_demo') get their own 63-second video (no audio track, so it autoplays muted):
// why SurFox is different, then the hand-off to Surf. Same player, but a demo sees it ONCE PER ACCOUNT, not once per sign-in: pressing
// Continue is remembered in localStorage (survives closing the browser) and on the server (a demo_activity video row with
// continued = true, so a different browser or device skips it too). Leaving partway without pressing Continue does not count.
const DEMO_VIDEO_SRC = `${process.env.PUBLIC_URL || ''}/howto/${encodeURIComponent('Demo Intro Video.mp4')}`;

const seenKeyFor = (session, user) =>
  `sfx_reviewer_intro_seen:${session?.user?.id || user?.id || 'anon'}:${session?.user?.last_sign_in_at || ''}`;

const readSeen = (key) => { try { return sessionStorage.getItem(key) === '1'; } catch (e) { return false; } };
const writeSeen = (key) => { try { sessionStorage.setItem(key, '1'); } catch (e) { /* not persisted: shows again on next load */ } };
const readLocal = (key) => { try { return localStorage.getItem(key) === '1'; } catch (e) { return false; } };
const writeLocal = (key) => { try { localStorage.setItem(key, '1'); } catch (e) { /* the server record still covers it */ } };

export default function ReviewerIntroVideo() {
  const { user, session } = useAuth();
  // Reviewers see the reviewer video; prospect demos see the demo video. Both are reviewer accounts underneath.
  const isDemo = user?.is_prospect_demo === true;
  const isReviewer = user?.is_reviewer === true;
  const key = seenKeyFor(session, user);
  const demoKey = `sfx_demo_intro_seen:${session?.user?.id || user?.id || 'anon'}`;
  const seenBefore = () => (isDemo ? readLocal(demoKey) : readSeen(key));
  const [open, setOpen] = useState(() => !readSeen(key));
  // Demos only: has this account already pressed Continue, in any browser? null = still asking (the video waits, at most 1.5 seconds).
  const [serverSeen, setServerSeen] = useState(null);
  const [needsTap, setNeedsTap] = useState(false);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  // The user object loads after the first render; re-check once we know who this is.
  useEffect(() => { setOpen(!seenBefore()); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, isDemo, demoKey]);

  useEffect(() => {
    if (!isDemo) return undefined;
    let live = true;
    const giveUp = setTimeout(() => { if (live) setServerSeen(false); }, 1500); // if the server is slow, show the video rather than nothing
    fetchWithCsrf('/api/demo-guide/context').then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (!live) return;
      clearTimeout(giveUp);
      if (d && d.intro_seen === true) writeLocal(demoKey);
      setServerSeen(!!(d && d.intro_seen === true));
    }).catch(() => { if (live) setServerSeen(false); });
    return () => { live = false; clearTimeout(giveUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo, demoKey]);

  // The video is on screen only when this person has not seen it. For a demo that also means the server has answered.
  const showing = isReviewer && open && !(isDemo && (serverSeen === null || serverSeen === true));

  useEffect(() => {
    if (!showing) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; // keep the page from scrolling behind the video
    return () => { document.body.style.overflow = previous; };
  }, [showing]);

  // Tell the demo Surf panel while the video is on screen, so it stays quiet until Continue (it sits above the video otherwise).
  useEffect(() => {
    if (!isDemo) return undefined;
    window.__sfxIntroOpen = showing;
    window.dispatchEvent(new CustomEvent('sfx:intro-state', { detail: { open: showing } }));
    return () => { window.__sfxIntroOpen = false; window.dispatchEvent(new CustomEvent('sfx:intro-state', { detail: { open: false } })); };
  }, [isDemo, showing]);

  // Demo only: how much of the video they actually watched, reported once, when they press Continue (or leave the page mid-video).
  const maxTimeRef = useRef(0);
  const endedRef = useRef(false);
  const reportedRef = useRef(false);
  const reportVideo = (continued = false) => {
    if (!isDemo || reportedRef.current) return;
    reportedRef.current = true;
    trackDemo({ type: 'video', watched_seconds: maxTimeRef.current, duration: videoRef.current?.duration || 0, ended: endedRef.current, continued });
  };
  useEffect(() => {
    if (!isDemo || !showing) return undefined;
    const onLeave = () => { reportVideo(false); flushDemoTrack(); };
    window.addEventListener('pagehide', onLeave);
    return () => window.removeEventListener('pagehide', onLeave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo, showing]);

  if (!showing) return null;

  const close = () => {
    reportVideo(true); // pressing Continue is what counts as having seen it
    if (isDemo) writeLocal(demoKey); else writeSeen(key);
    setOpen(false);
  };
  // Play/Pause is one button that follows the video's real state; at the end, Play starts it over.
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused || v.ended) tryPlay(); else v.pause();
  };
  const restart = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    tryPlay();
  };
  const tryPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.then(() => setNeedsTap(false)).catch(() => setNeedsTap(true));
  };

  return createPortal(
    <div className={`fixed inset-0 z-[10000] flex items-center justify-center overflow-y-auto bg-black/80 p-4${isDemo ? ' lg:pr-[396px]' : ''}`}
      role="dialog" aria-modal="true" aria-label="Welcome video">
      <div className="flex max-w-full flex-col items-center gap-4">
        <div className="relative">
          <video
            ref={videoRef}
            src={isDemo ? DEMO_VIDEO_SRC : VIDEO_SRC}
            autoPlay
            muted
            playsInline
            preload="auto"
            onLoadedData={tryPlay}
            onPlay={() => { setNeedsTap(false); setPlaying(true); }}
            onPause={() => setPlaying(false)}
            onEnded={() => { endedRef.current = true; setPlaying(false); }}
            onTimeUpdate={(e) => { if (e.currentTarget.currentTime > maxTimeRef.current) maxTimeRef.current = e.currentTarget.currentTime; }}
            style={{ maxWidth: '96vw', maxHeight: '80vh' }}
            className="rounded-xl bg-black shadow-2xl"
          />
          {needsTap && (
            <button type="button" onClick={tryPlay} aria-label="Play the video"
              className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/40">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow-lg">
                <Play className="ml-1 h-7 w-7" />
              </span>
            </button>
          )}
        </div>
        <p className="max-w-xl text-center text-sm text-slate-200">
          {isDemo
            ? 'Your demo is already built from your website. Press Continue and Surf will take you to your first test conversation.'
            : 'Your review account is already set up, so you skip onboarding. Continue to explore it with sample data.'}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button type="button" onClick={restart}
            className="inline-flex items-center gap-2 rounded-lg border border-white/30 bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/20">
            <RotateCcw className="h-4 w-4" /> Restart
          </button>
          <button type="button" onClick={togglePlay}
            className="inline-flex items-center gap-2 rounded-lg border border-white/30 bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/20">
            {playing ? <><Pause className="h-4 w-4" /> Pause</> : <><Play className="h-4 w-4" /> Play</>}
          </button>
          <button type="button" onClick={close} autoFocus
            className="rounded-lg bg-indigo-600 px-8 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-indigo-700">
            Continue
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
